import type { StylesetFill, StylesetLook } from "../../../shared/factset/stylesetChartFormat";
import { CHART_PART, JSZip, readValue } from "./preslinkChartAxis";

const AXIS = /<c:(valAx|catAx|dateAx|serAx)>[\s\S]*?<\/c:\1>/g;
const LINE_GROUP = /<c:(lineChart|line3DChart|scatterChart)>[\s\S]*?<\/c:\1>/g;
const SHAPE_FILL = /<a:noFill\s*\/>|<a:(solidFill|gradFill|pattFill|blipFill)\b[\s\S]*?<\/a:\1>/;
/** The series' own spPr precedes its markers, points, labels and values. */
const SERIES_SHAPE =
  /^((?:(?!<c:(?:marker|dPt|dLbls|trendline|errBars|cat|val|xVal|yVal)\b)[\s\S])*?)<c:spPr>([\s\S]*?)<\/c:spPr>/;
/** 1pt, which the frame's downscaled render shows close to the pixel-wide lines of ChartCreator's renders. */
const MIN_SERIES_LINE_EMU = 12700;

function solid(color: string): string {
  return `<a:solidFill><a:srgbClr val="${color}"/></a:solidFill>`;
}

function fillXml(fill: StylesetFill | undefined): string {
  if (!fill) return "<a:noFill/>";
  if ("color" in fill) return solid(fill.color);
  return (
    `<a:gradFill rotWithShape="1"><a:gsLst><a:gs pos="0"><a:srgbClr val="${fill.from}"/></a:gs>` +
    `<a:gs pos="100000"><a:srgbClr val="${fill.to}"/></a:gs></a:gsLst>` +
    `<a:lin ang="${Math.round(fill.angle * 60000)}" scaled="0"/></a:gradFill>`
  );
}

/** Replaces the shape fill, which precedes the outline, keeping the outline as it was. */
function withFill(spPr: string, fill: string): string {
  const outline = spPr.search(/<a:ln\b/);
  const head = outline < 0 ? spPr : spPr.slice(0, outline);
  const tail = outline < 0 ? "" : spPr.slice(outline);
  const filled = SHAPE_FILL.test(head)
    ? head.replace(SHAPE_FILL, () => fill)
    : head.replace(/^<c:spPr>/, () => `<c:spPr>${fill}`);
  return filled + tail;
}

function chartSpaceFilled(chartXml: string, fill: string): string {
  const chartEnd = chartXml.indexOf("</c:chart>");
  if (chartEnd < 0) return chartXml;
  const at = chartEnd + "</c:chart>".length;
  const existing = /^\s*<c:spPr>[\s\S]*?<\/c:spPr>/.exec(chartXml.slice(at));
  if (!existing) return `${chartXml.slice(0, at)}<c:spPr>${fill}</c:spPr>${chartXml.slice(at)}`;
  const spPr = existing[0].trimStart();
  const start = at + existing[0].length - spPr.length;
  return chartXml.slice(0, start) + withFill(spPr, fill) + chartXml.slice(start + spPr.length);
}

/** plotArea's own spPr follows its axes, ahead of only an extLst. */
function plotAreaFilled(plotArea: string, fill: string): string {
  const close = plotArea.lastIndexOf("</c:plotArea>");
  const body = plotArea.slice(0, close);
  const extension = /<c:extLst>[\s\S]*<\/c:extLst>\s*$/.exec(body);
  const before = extension ? body.slice(0, extension.index) : body;
  const own = /<c:spPr>(?:(?!<c:spPr>)[\s\S])*?<\/c:spPr>\s*$/.exec(before);
  const rest = plotArea.slice(before.length);
  if (!own) return `${before}<c:spPr>${fill}</c:spPr>${rest}`;
  return before.slice(0, own.index) + withFill(own[0], fill) + rest;
}

/** Visible gridlines take the theme color; hidden ones stay hidden. */
function gridlinesColored(axis: string, look: StylesetLook): string {
  const color = /^[bt]$/.test(readValue(axis, "axPos") ?? "") ? look.xGridColor : look.yGridColor;
  return axis.replace(/<c:majorGridlines>[\s\S]*?<\/c:majorGridlines>/, (gridlines) =>
    gridlines.replace(/(<a:ln\b[^>]*>[\s\S]*?)<a:solidFill>[\s\S]*?<\/a:solidFill>/, (_line, start: string) => start + solid(color)),
  );
}

/**
 * Only drawn lines are widened; marker-only endpoints and hidden helpers keep
 * their noFill outline. `atLeast` leaves lines already that wide as they are.
 */
function seriesWidened(group: string, widthEmu: number, atLeast = false): string {
  return group.replace(/<c:ser>[\s\S]*?<\/c:ser>/g, (series) =>
    series.replace(SERIES_SHAPE, (shape, before: string, spPr: string) => {
      const widened = spPr.replace(/<a:ln\b([^>]*)>([\s\S]*?)<\/a:ln>/, (line, attributes: string, body: string) => {
        if (!/^\s*<a:solidFill>/.test(body)) return line;
        if (atLeast && Number(/\bw="(\d+)"/.exec(attributes)?.[1] ?? 0) >= widthEmu) return line;
        const sized = /\bw="\d+"/.test(attributes)
          ? attributes.replace(/\bw="\d+"/, `w="${widthEmu}"`)
          : `${attributes} w="${widthEmu}"`;
        return `<a:ln${sized}>${body}</a:ln>`;
      });
      return widened === spPr ? shape : `${before}<c:spPr>${widened}</c:spPr>`;
    }),
  );
}

function themedChart(chartXml: string, look: StylesetLook): string {
  const plotArea = /<c:plotArea>[\s\S]*<\/c:plotArea>/.exec(chartXml);
  if (!plotArea) return chartXml;
  let plotXml = plotAreaFilled(plotArea[0], fillXml(look.plotFill)).replace(AXIS, (axis) => gridlinesColored(axis, look));
  const widthEmu = look.seriesLineWidthEmu;
  if (widthEmu) plotXml = plotXml.replace(LINE_GROUP, (group) => seriesWidened(group, widthEmu));
  const xml = chartXml.slice(0, plotArea.index) + plotXml + chartXml.slice(plotArea.index + plotArea[0].length);
  return chartSpaceFilled(xml, solid(look.chartFill));
}

async function rewriteCharts(workbookBase64: string, rewrite: (chartXml: string) => string): Promise<string> {
  const zip = await JSZip.loadAsync(workbookBase64, { base64: true });
  let changed = false;
  for (const chartPath of Object.keys(zip.files).filter((path) => CHART_PART.test(path))) {
    const chartXml = await zip.file(chartPath)?.async("string");
    if (!chartXml) continue;
    const rewritten = rewrite(chartXml);
    if (rewritten === chartXml) continue;
    zip.file(chartPath, rewritten);
    changed = true;
  }
  return changed ? zip.generateAsync({ type: "base64", compression: "DEFLATE" }) : workbookBase64;
}

/** Writes the parts of a styleset's look that ChartCreator's Excel export drops. */
export function applyStylesetLook(workbookBase64: string, look: StylesetLook): Promise<string> {
  return rewriteCharts(workbookBase64, (chartXml) => themedChart(chartXml, look));
}

/**
 * ChartCreator's Excel export draws series lines as 0.25pt hairlines where its
 * own renders draw them about a pixel wide. A styleset's width, applied later,
 * still wins.
 */
export function widenHairlineSeries(workbookBase64: string): Promise<string> {
  return rewriteCharts(workbookBase64, (chartXml) =>
    chartXml.replace(LINE_GROUP, (group) => seriesWidened(group, MIN_SERIES_LINE_EMU, true)),
  );
}
